import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaHandshake, FaStar } from "react-icons/fa";
import { RiNumbersFill } from "react-icons/ri";
import {
  countAssociations,
  countClientAssociations,
} from "../../../../services/associationsServices";
import {
  countVisites,
  countVisitesAccueilToday,
} from "../../../../services/visitesServices";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Number(value || 0)
  );

const StatisticsCards = () => {
  const navigate = useNavigate();

  const [associationsCount, setAssociationsCount] = useState(0);
  const [associationStatusCounts, setAssociationStatusCounts] = useState({
    Client: 0,
    "Client SAAS": 0,
    "En Test": 0,
    Prospect: 0,
  });
  const [visitesCount, setVisitesCount] = useState(0);
  const [visitesAccueilTodayCount, setVisitesAccueilTodayCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true);

        const [
          associationsResponse,
          statusCountsResponse,
          visitesResponse,
          visitesAccueilTodayResponse,
        ] = await Promise.all([
          countAssociations(),
          countClientAssociations(),
          countVisites(),
          countVisitesAccueilToday(),
        ]);

        setAssociationsCount(associationsResponse?.count || 0);
        setAssociationStatusCounts({
          Client: statusCountsResponse?.Client || 0,
          "Client SAAS": statusCountsResponse?.["Client SAAS"] || 0,
          "En Test": statusCountsResponse?.["En Test"] || 0,
          Prospect: statusCountsResponse?.Prospect || 0,
        });
        setVisitesCount(visitesResponse?.count || 0);
        setVisitesAccueilTodayCount(visitesAccueilTodayResponse?.count || 0);
      } catch (error) {
        console.error("Erreur lors de la récupération des statistiques", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  const totalClients = useMemo(() => {
    return (
      Number(associationStatusCounts["Client"] || 0) +
      Number(associationStatusCounts["Client SAAS"] || 0)
    );
  }, [associationStatusCounts]);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
      <DashboardStatCard
        title="Associations / clubs"
        value={formatNumber(associationsCount)}
        subtitle="Base enregistrée"
        icon={<FaStar />}
        tone="purple"
        loading={loading}
        onClick={() => navigate("/appli/listing-clients")}
      />

      <DashboardStatCard
        title="Clients"
        value={formatNumber(totalClients)}
        subtitle="Clients actifs"
        icon={<FaHandshake />}
        tone="green"
        loading={loading}
        onClick={() => navigate("/appli/listing-clients")}
        extra={
          <div className="mt-4 flex flex-wrap gap-2">
            <MiniPill label="Standard" value={associationStatusCounts["Client"]} />
            <MiniPill
              label="SaaS"
              value={associationStatusCounts["Client SAAS"]}
            />
            <MiniPill label="Test" value={associationStatusCounts["En Test"]} />
            <MiniPill label="Prospects" value={associationStatusCounts["Prospect"]} />
          </div>
        }
      />

      <DashboardStatCard
        title="Visites totales"
        value={formatNumber(visitesCount)}
        subtitle="Toutes pages confondues"
        icon={<RiNumbersFill />}
        tone="blue"
        loading={loading}
      />

      <DashboardStatCard
        title="Accueil aujourd’hui"
        value={formatNumber(visitesAccueilTodayCount)}
        subtitle="Trafic du jour"
        icon={<RiNumbersFill />}
        tone="pink"
        loading={loading}
      />
    </div>
  );
};

export default StatisticsCards;

function DashboardStatCard({
  title,
  value,
  subtitle,
  icon,
  tone = "blue",
  loading = false,
  onClick,
  extra,
}) {
  const tones = {
    purple: {
      icon: "bg-violet-100 text-violet-700",
      glow: "hover:border-violet-200",
    },
    green: {
      icon: "bg-emerald-100 text-emerald-700",
      glow: "hover:border-emerald-200",
    },
    blue: {
      icon: "bg-sky-100 text-sky-700",
      glow: "hover:border-sky-200",
    },
    pink: {
      icon: "bg-rose-100 text-rose-700",
      glow: "hover:border-rose-200",
    },
  };

  const style = tones[tone] || tones.blue;
  const clickable = typeof onClick === "function";

  const Component = clickable ? "button" : "div";

  return (
    <Component
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      className={cn(
        "w-full rounded-[30px] border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200",
        "hover:-translate-y-[2px] hover:shadow-md",
        style.glow,
        clickable && "cursor-pointer"
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>

          <div className="mt-3">
            {loading ? (
              <div className="h-10 w-28 animate-pulse rounded-xl bg-slate-100" />
            ) : (
              <p className="text-[2rem] font-extrabold leading-none tracking-tight text-[#123B57]">
                {value}
              </p>
            )}
          </div>

          <p className="mt-2 text-sm text-slate-400">{subtitle}</p>
        </div>

        <div
          className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-xl",
            style.icon
          )}
        >
          {icon}
        </div>
      </div>

      {extra ? extra : null}
    </Component>
  );
}

function MiniPill({ label, value }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
      <span>{label}</span>
      <span className="text-slate-900">{formatNumber(value)}</span>
    </div>
  );
}